/**
 * Live reader/writer for the MFX Estimating Suite's shared Firestore docs
 * (`mfx_estimating_suite/*`) — the SAME source of truth the CSR app
 * (csr.microflexfilm.com) uses, so the unified CRM and the current app stay
 * in sync during the transition.
 *
 * Storage model (verbatim from the CSR app):
 *   customers → { items: Customer[] }
 *   meta      → { clientCodeSeq, quoteNumSeq, reqSeq, qSeq }
 * Writes are whole-doc `setDoc(..., { items })` — matching the CSR app's
 * `CLOUD.collection('mfx_estimating_suite').doc(name).set(data)` model.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

const SUITE = 'mfx_estimating_suite';

export interface Customer {
  n: string;                 // Company Name (required)
  code?: string;             // Client Code MFX-MMYY-####
  ct?: string; act?: string; // Primary / Alternate contact
  ph?: string; aph?: string; fx?: string; // Phone / Alt phone / Fax
  tm?: string; tc?: string;  // Payment Terms / Terms Category
  rp?: string; rs?: string;  // Sales Rep / Resale No.
  bst?: string; sst?: string;// Bill State / Ship State
  tx?: string;               // Taxable
  bt?: string; sh?: string;  // Bill To / Ship To
  [k: string]: string | undefined;
}

export interface CrmField { k: keyof Customer & string; label: string; full?: boolean; area?: boolean; }

// Exact schema + order from the CSR app's CRM_FIELDS.
export const CRM_FIELDS: CrmField[] = [
  { k: 'n', label: 'Company Name', full: true },
  { k: 'code', label: 'Client Code (MFX-MMYY-#)' },
  { k: 'ct', label: 'Primary Contact' },
  { k: 'act', label: 'Alternate Contact' },
  { k: 'ph', label: 'Phone' },
  { k: 'aph', label: 'Alternate Phone' },
  { k: 'fx', label: 'Fax' },
  { k: 'tm', label: 'Payment Terms' },
  { k: 'tc', label: 'Terms Category' },
  { k: 'rp', label: 'Sales Rep' },
  { k: 'rs', label: 'Resale No.' },
  { k: 'bst', label: 'Bill State' },
  { k: 'sst', label: 'Ship State' },
  { k: 'tx', label: 'Taxable' },
  { k: 'bt', label: 'Bill To', area: true, full: true },
  { k: 'sh', label: 'Ship To', area: true, full: true },
];

export function customerMatches(c: Customer, q: string): boolean {
  if (!q) return true;
  return `${c.n} ${c.ct || ''} ${c.act || ''} ${c.ph || ''} ${c.aph || ''} ${c.rp || ''}`.toLowerCase().includes(q);
}

// MM + YY (2-digit each), matching the CSR app's client-code prefix.
function mmYY(d = new Date()): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear() % 100).padStart(2, '0');
  return mm + yy;
}
export function makeClientCode(seq: number): string {
  return `MFX-${mmYY()}-${String(seq).padStart(4, '0')}`;
}

export interface SuiteMeta { clientCodeSeq?: number; quoteNumSeq?: number; reqSeq?: number; qSeq?: number; soSeq?: number; woSeq?: number; }

export interface CustomersState {
  customers: Customer[];
  meta: SuiteMeta;
  loading: boolean;
  live: boolean;
  error: string | null;
}

/** Live-subscribe to the customers + meta docs. */
export function useCustomers(): CustomersState {
  const [state, setState] = useState<CustomersState>({ customers: [], meta: {}, loading: true, live: false, error: null });

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, SUITE, 'meta'))
      .then((m) => { if (alive && m.exists()) setState((s) => ({ ...s, meta: (m.data() as SuiteMeta) || {} })); })
      .catch(() => {});
    const unsub = onSnapshot(
      doc(db, SUITE, 'customers'),
      (snap) => {
        if (!alive) return;
        const data = snap.exists() ? (snap.data() as { items?: Customer[] }) : {};
        const items = Array.isArray(data.items) ? data.items : [];
        setState((s) => ({ ...s, customers: items, loading: false, live: true, error: null }));
      },
      (e) => { if (alive) setState((s) => ({ ...s, loading: false, live: false, error: e?.message || 'Could not load customers' })); },
    );
    return () => { alive = false; unsub(); };
  }, []);

  return state;
}

/** Persist the whole customers array back to the shared doc (same as CSR app). */
export async function saveCustomers(items: Customer[]): Promise<void> {
  await setDoc(doc(db, SUITE, 'customers'), { items });
}

/** Bump and persist the client-code sequence; returns the new code. */
export async function issueClientCode(meta: SuiteMeta): Promise<{ code: string; nextSeq: number }> {
  const seq = (meta.clientCodeSeq || 1);
  const code = makeClientCode(seq);
  // Write ONLY the key we own — spreading `meta` here could write back a STALE
  // quoteNumSeq/reqSeq/qSeq captured at page load and cause duplicate IDs.
  await setDoc(doc(db, SUITE, 'meta'), { clientCodeSeq: seq + 1 }, { merge: true });
  return { code, nextSeq: seq + 1 };
}

export function customerToCsvRow(c: Customer): string {
  const q = (s: unknown) => '"' + String(s == null ? '' : s).replace(/"/g, '""') + '"';
  return CRM_FIELDS.map((f) => q(c[f.k] || '')).join(',');
}
export function customersCsv(items: Customer[]): string {
  const q = (s: unknown) => '"' + String(s == null ? '' : s).replace(/"/g, '""') + '"';
  const head = CRM_FIELDS.map((f) => q(f.label)).join(',');
  return [head, ...items.map(customerToCsvRow)].join('\r\n');
}
